/**
 * Host-Infrastructure page — superuser-only.
 *
 * The page is about CRONS — what they do, when they last ran, where
 * they live. The two hosts (linza-deployments + linza-dev) are just
 * a grouping axis. Layout:
 *
 *   1. Page header (via PageHeaderContext)
 *   2. One cron table — Host | Cron | Schedule | Status | Last updated
 *      Rows visually grouped by host (label only on the first row of
 *      each group, divider between groups).
 *   3. Compact install strip — one row per host with the BE-rendered
 *      install-curl and a copy button. Below: dev-keys-path note.
 *
 * No per-host descriptive blurbs. No overall-status badges (the FE
 * can't honestly assert "this host is working" — it only knows about
 * the cron heartbeats, and the cron-status table already conveys
 * those per-row). Compact-first per Alexey 2026-05-23.
 */

import { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import {
  AlertTriangle,
  Check,
  Copy,
  FileKey,
  Loader2,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import {
  getCronStatus,
  getHostInstallCurl,
  getRootScriptHeartbeats,
  type CronCheckRow,
  type CronStatus,
  type CronStatusResponse,
  type RootScriptHeartbeat,
  type ServerKey,
} from '@/api/hostStatus';

const swrKeyFor = (server: ServerKey): string =>
  `host-status:cron-status:${server}`;

const installCurlSwrKeyFor = (server: ServerKey): string =>
  `host-status:install-curl:${server}`;

type T = (k: string, p?: Record<string, string | number>) => string;

// Stable host order. Table rows + install-strip rows both follow this.
const HOSTS: ServerKey[] = ['depl', 'dev'];

/** Plain hostname per host — operator-facing label.
 *  Pre-iteration this was a localized "Сервер деплоя (deployments)"
 *  string, but the hostnames are themselves the canonical identifier
 *  (they appear in URLs, SSH targets, etc.) so localization adds
 *  noise. */
const HOST_LABEL: Record<ServerKey, string> = {
  depl: 'linza-deployments',
  dev: 'linza-dev',
};

interface UnifiedRow extends CronCheckRow {
  host: ServerKey;
  isFirstOfGroup: boolean;
}

export default function HostInfraPage() {
  const { t } = useT();
  usePageTitle(t('system.hostInfra.title'), t('system.hostInfra.subtitle'));

  const depl = useSWR<CronStatusResponse>(
    swrKeyFor('depl'),
    () => getCronStatus('depl').then(r => r.data),
    { revalidateOnFocus: false },
  );
  const dev = useSWR<CronStatusResponse>(
    swrKeyFor('dev'),
    () => getCronStatus('dev').then(r => r.data),
    { revalidateOnFocus: false },
  );

  // Push-based heartbeats — carry the rich payload (rc, log_tail, versions) for
  // crons that report it (model_deploy on the depl host). Keyed by script name = row id.
  const heartbeats = useSWR<RootScriptHeartbeat[]>(
    'host-status:root-heartbeats',
    () => getRootScriptHeartbeats().then(r => r.data.heartbeats),
    { revalidateOnFocus: false },
  );
  const hbByName: Record<string, RootScriptHeartbeat> = {};
  for (const h of heartbeats.data ?? []) hbByName[h.name] = h;
  const [openLog, setOpenLog] = useState<string | null>(null);

  const allLoading = !depl.data && !dev.data && (depl.isLoading || dev.isLoading);
  const anyError = depl.error != null && dev.error != null;

  const unifiedRows: UnifiedRow[] = [];
  for (const host of HOSTS) {
    const swr = host === 'depl' ? depl : dev;
    const checks = swr.data?.checks ?? [];
    checks.forEach((row, idx) => {
      unifiedRows.push({ ...row, host, isFirstOfGroup: idx === 0 });
    });
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-4 px-4 py-6">
      <Card variant="section">
        <CardContent className="space-y-4">
          {/* Table — primary content */}
          {anyError && !depl.data && !dev.data && (
            <ServerStatusNotice variant="card" error={depl.error} onRetry={() => { void depl.mutate(); void dev.mutate(); }} />
          )}
          {allLoading && (
            <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('common.loading')}
            </div>
          )}
          {!allLoading && unifiedRows.length === 0 && !anyError && (
            <div className="rounded-md border border-dashed bg-muted/10 px-3 py-3 text-xs text-muted-foreground">
              {t('system.hostInfra.emptyList')}
            </div>
          )}
          {unifiedRows.length > 0 && (
            <UnifiedCronTable
              rows={unifiedRows}
              hbByName={hbByName}
              openLog={openLog}
              setOpenLog={setOpenLog}
              t={t}
            />
          )}

          {/* Install strips — secondary, compact */}
          <div className="space-y-2 border-t border-border/60 pt-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('system.hostInfra.installHeading')}
            </div>
            {HOSTS.map(host => (
              <InstallStripRow key={host} server={host} t={t} />
            ))}
            <DevKeysPathNote t={t} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── compact install strip ─────────────────────────────────────────

function InstallStripRow({ server, t }: { server: ServerKey; t: T }) {
  const { data: curl, error: curlError } = useSWR<string>(
    installCurlSwrKeyFor(server),
    () => getHostInstallCurl(server).then(r => r.data.install_curl),
    // 10-min token TTL — refresh on tab focus so a returning operator
    // doesn't paste an expired one.
    { revalidateOnFocus: true, dedupingInterval: 30_000 },
  );

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!curl) return;
    try {
      await navigator.clipboard.writeText(curl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard.writeText fails in non-HTTPS dev contexts; ignore.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
        {HOST_LABEL[server]}
      </span>
      <pre className="flex-1 overflow-auto rounded-md bg-muted/40 px-2.5 py-1.5 font-mono text-[11px] leading-snug">
        {curlError != null
          ? t('system.hostInfra.installCurlError')
          : curl ?? t('common.loading')}
      </pre>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={copy}
        disabled={!curl}
        title={t('common.copy')}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
    </div>
  );
}

/** Compact one-liner pointing at the OUTBOUND dev-keys file on
 *  linza-dev. NOT the file the sync cron manages (that's
 *  .ssh/authorized_keys, INBOUND). This is the manually-maintained
 *  authorized_keys.example that the LC setup-curl pulls once at
 *  install to seed each LC's own authorized_keys. */
function DevKeysPathNote({ t }: { t: T }) {
  return (
    <div className="flex items-start gap-2 pt-1 text-[11px] text-muted-foreground">
      <FileKey className="size-3 shrink-0 translate-y-0.5" />
      <span>
        {t('system.hostInfra.devKeysPath.note')}{' '}
        <code className="rounded bg-muted/40 px-1 py-0.5 font-mono">
          linza-dev:/home/linza/authorized_keys.example
        </code>
      </span>
    </div>
  );
}

// ── unified table ─────────────────────────────────────────────────

function UnifiedCronTable({
  rows, hbByName, openLog, setOpenLog, t,
}: {
  rows: UnifiedRow[];
  hbByName: Record<string, RootScriptHeartbeat>;
  openLog: string | null;
  setOpenLog: (id: string | null) => void;
  t: T;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">
              {t('system.hostInfra.colHost')}
            </th>
            <th className="px-3 py-2 text-left font-semibold">
              {t('system.hostInfra.colScript')}
            </th>
            <th className="px-3 py-2 text-left font-semibold">
              {t('system.hostInfra.colSchedule')}
            </th>
            <th className="w-12 px-3 py-2 text-left font-semibold">
              {t('system.hostInfra.colStatus')}
            </th>
            <th className="px-3 py-2 text-left font-semibold">
              {t('system.hostInfra.colMtime')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const rowKey = `${row.host}:${row.id}`;
            return (
              <UnifiedCronTableRow
                key={rowKey}
                row={row}
                hb={hbByName[row.id]}
                open={openLog === rowKey}
                onToggle={() => setOpenLog(openLog === rowKey ? null : rowKey)}
                t={t}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UnifiedCronTableRow({
  row, hb, open, onToggle, t,
}: {
  row: UnifiedRow;
  hb?: RootScriptHeartbeat;
  open: boolean;
  onToggle: () => void;
  t: T;
}) {
  const purposeKey = `system.hostInfra.purpose.${row.id}`;
  const purposeFromI18n = t(purposeKey);
  const purpose = purposeFromI18n === purposeKey ? row.purpose : purposeFromI18n;
  const schedule = parseCronSchedule(row.expected_line);
  // Rich heartbeat payload (model_deploy) → offer an expandable log.
  const versions = hb ? Object.entries(hb.deployed_versions || {}) : [];
  const hasDetail = !!hb && (!!hb.log_tail || !!hb.summary || versions.length > 0);

  const groupBorder = row.isFirstOfGroup
    ? 'border-t-2 border-border'
    : 'border-t border-border/40';

  return (
    <>
      <tr className={`${groupBorder} hover:bg-muted/20`}>
        <td className="px-3 py-2 font-mono text-[11px] tabular-nums">
          {row.isFirstOfGroup ? HOST_LABEL[row.host] : ''}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-medium">{row.label}</span>
            {hb && hb.rc != null && (
              <span
                className={`rounded px-1 text-[10px] font-semibold ${
                  hb.rc === 0
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'bg-destructive/15 text-destructive'
                }`}
              >
                rc={hb.rc}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">{purpose}</div>
          {hb?.summary && (
            <div className="text-[11px] text-foreground/70">{hb.summary}</div>
          )}
        </td>
        <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-muted-foreground">
          {schedule}
        </td>
        <td className="px-3 py-2">
          <StatusIcon status={row.status} />
        </td>
        <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
          <div className="flex items-center gap-2">
            {row.mtime_iso ? formatMtime(row.mtime_iso, t) : '—'}
            {hasDetail && (
              <button
                type="button"
                className="text-primary underline"
                onClick={onToggle}
              >
                {open ? t('system.hostInfra.hideLog') : t('system.hostInfra.showLog')}
              </button>
            )}
          </div>
        </td>
      </tr>
      {open && hasDetail && (
        <tr className="border-t border-border/40 bg-muted/10">
          <td colSpan={5} className="px-3 py-2">
            {versions.length > 0 && (
              <div className="mb-2 text-[11px] text-muted-foreground">
                {t('system.hostInfra.deployedVersions')}:{' '}
                {versions.map(([n, v]) => `${n}=${String(v).slice(0, 8)}`).join(', ')}
              </div>
            )}
            {hb?.log_tail && (
              <pre className="max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[10px] leading-relaxed text-foreground">
                {hb.log_tail}
              </pre>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function parseCronSchedule(expectedLine: string): string {
  const trimmed = (expectedLine ?? '').trim();
  if (!trimmed) return '—';
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 5) return '—';
  return tokens.slice(0, 5).join(' ');
}

function StatusIcon({ status }: { status: CronStatus }) {
  // UNKNOWN (probe_kind=never) → "cron-quiet by design". Surface as healthy:
  // the cron exists, it just only writes when it has work to do, so absence
  // of recent log activity is the EXPECTED state. Per team-lead 2026-05-21.
  if (status === 'OK' || status === 'UNKNOWN') {
    return <Check className="size-4 text-emerald-600 dark:text-emerald-400" />;
  }
  if (status === 'STALE') {
    return <AlertTriangle className="size-4 text-amber-500 dark:text-amber-400" />;
  }
  return <XCircle className="size-4 text-destructive" />;
}

function formatMtime(iso: string, t: (k: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ${t('system.hostInfra.ago')}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ${t('system.hostInfra.ago')}`;
  const h = Math.round(m / 60);
  return `${h}h ${t('system.hostInfra.ago')}`;
}
